import { IFRAME_TYPE } from '@pp/core/src/store/slices/launcher/types';

import { OPERATOR_GAME_IDS_TO_HIDE } from '../helpers/constants';
import { getLobbyURL, getLauncherLobbyURL } from '../helpers/utils';
import { LauncherSession } from '../session/LauncherSession';

import { LauncherAction, LauncherActionType } from './actions';
import type { LauncherState, LauncherTableInfo } from './types';

const updateTable = (
  tables: LauncherTableInfo[],
  tableId: string,
  updateFn: (table: LauncherTableInfo) => LauncherTableInfo
): LauncherTableInfo[] => {
  return tables.map((table) => (table.tableId === tableId ? updateFn(table) : table));
};

const hasIframeType = (tables: LauncherTableInfo[], iframeType: string): boolean => {
  return tables.some((table) => table.iframeType === iframeType);
};

export const launcherReducer = (state: LauncherState, action: LauncherAction): LauncherState => {
  switch (action.type) {
    case LauncherActionType.SET_TABLES: {
      const tables = action.payload?.tables?.map((table) => ({ ...table, isActive: false })) || [];
      return { ...state, tables };
    }

    case LauncherActionType.ADD_TABLE: {
      const { tableId, url } = action.payload || {};
      if (!tableId || !url) return state; // Ensure payload is valid
      const tables = state.tables.filter((table) => table.tableId !== IFRAME_TYPE.LOBBY);
      const newTable: LauncherTableInfo = {
        tableId,
        url,
        iframeType: IFRAME_TYPE.TABLE,
        showClose: true,
      };
      return { ...state, tables: [...tables, newTable] };
    }

    case LauncherActionType.REMOVE_TABLE:
      return {
        ...state,
        tables: state.tables.filter((table) => table.tableId !== action.payload?.tableId),
      };

    case LauncherActionType.SET_ACTIVE_TABLE:
      return {
        ...state,
        tables: state.tables.map((table) => ({
          ...table,
          isActive: action.payload?.tableId === table.tableId,
        })),
      };

    case LauncherActionType.SET_OPERATOR_GAME_ID:
      return {
        ...state,
        tables: updateTable(state.tables, action.payload?.tableId || '', (table) => ({
          ...table,
          operatorGameId: action.payload?.operatorGameId || '',
          showClose: false,
        })),
      };

    case LauncherActionType.OPEN_LOBBY: {
      if (hasIframeType(state.tables, IFRAME_TYPE.LOBBY)) {
        return state;
      }

      const tableUrl = state.tables[0]?.url || '';
      const hiddenGamesOperatorIds = state.tables
        .map((table) => table.operatorGameId)
        .concat(OPERATOR_GAME_IDS_TO_HIDE)
        .join(',');

      const newLobby: LauncherTableInfo = {
        tableId: IFRAME_TYPE.LOBBY,
        url: getLauncherLobbyURL(tableUrl, hiddenGamesOperatorIds),
        iframeType: IFRAME_TYPE.LOBBY,
        showClose: true,
      };

      return { ...state, tables: [...state.tables, newLobby] };
    }

    case LauncherActionType.OPEN_MTB: {
      const { url } = action.payload || {};
      if (!url) return state; // Ensure payload is valid
      const newMtb: LauncherTableInfo = {
        tableId: IFRAME_TYPE.MTB,
        url,
        iframeType: IFRAME_TYPE.MTB,
      };
      return { ...state, tables: [...state.tables, newMtb] };
    }

    case LauncherActionType.LOBBY_LOADED:
      return {
        ...state,
        tables: updateTable(state.tables, IFRAME_TYPE.LOBBY, (table) => ({
          ...table,
          showClose: false,
        })),
      };

    case LauncherActionType.LOBBY_CLOSED: {
      if (state.tables.length === 1) {
        const lobbyUrl = getLobbyURL(LauncherSession.getSession()?.baseUrl || '');
        if (lobbyUrl) {
          window.location.href = lobbyUrl;
        } else {
          window.history.back();
        }
      }

      return {
        ...state,
        tables: state.tables.filter((table) => table.tableId !== IFRAME_TYPE.LOBBY),
      };
    }

    case LauncherActionType.GAME_LOADED:
      return { ...state, showFullscreen: true };

    case LauncherActionType.TOGGLE_MTB:
      return {
        ...state,
        tables: updateTable(state.tables, IFRAME_TYPE.MTB, (table) => ({
          ...table,
          isMtbMinimized: action.payload?.isMinimize || false,
        })),
      };

    case LauncherActionType.REPLACE_TABLE: {
      // This is for the Join Table functionality in the In Game MTB where the base table is changed and MTB is reconnected with new base table without reloading.
      const { tableId, url, operatorGameId } = action.payload || {};
      if (!tableId || !operatorGameId || !url) return state; // Ensure payload is valid
      return {
        ...state,
        tables: state.tables.map((table, index) => {
          if (index === 0) {
            table.url = url;
            table.operatorGameId = operatorGameId;
            table.tableId = tableId;
          }
          return table;
        }),
      };
    }

    case LauncherActionType.BASE_TABLE_CHANGED: {
      const tableId = action.payload?.tableId;
      if (!tableId) return state;
      return {
        ...state,
        tables: state.tables.map((table, index) => {
          if (index === 1 && table.iframeType === IFRAME_TYPE.MTB) {
            const url = new URL(table.url);
            const searchParams = url.searchParams;
            searchParams.set('table_id', tableId);
            table.url = url.toString();
          }
          return table;
        }),
      };
    }

    default:
      return state;
  }
};
